import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import {
  schedulesApi,
  type CalendarDetail,
  type TimezoneOption,
} from "../api/schedulesApi";

type LoadState =
  | { status: "loading" }
  | {
      status: "ready";
      detail: CalendarDetail;
      dates: Set<string>;
    }
  | { status: "error"; message: string };

interface Draft {
  name: string;
  description: string;
  /**
   * Empty string means "inherit tenant default"; any non-empty value must
   * match a `TimezoneOption.id`. We keep it as part of the draft (rather
   * than a separate select-value) so Save picks up the change via the
   * existing isDirty comparison.
   */
  timezoneId: string;
}

function toDraft(d: CalendarDetail): Draft {
  // Normalise the stored "UTC" (explicit) to "" (implicit) in the UI so
  // calendars that have "UTC" written to the column by legacy tooling
  // show up on our synthetic top option instead of a confusing "(not
  // installed)" entry, and so a round-trip through the picker doesn't
  // flip the server value back and forth.
  const tz = d.timezoneId ?? "";
  return {
    name: d.name ?? "",
    description: d.description ?? "",
    timezoneId: tz === "UTC" ? "" : tz,
  };
}

/**
 * Edit surface for a single calendar. Three tiles:
 *   1. Details (name + description, timezone is read-only).
 *   2. A month calendar next to a scrollable list of marked dates. Click
 *      a day to toggle; click an "×" on a list row to unmark without
 *      scrubbing through a year to find it.
 *   3. Danger zone delete, matching the Weekplan / Application pages.
 *
 * State-wise we keep one canonical Set<string> of ISO dates in memory
 * and optimistically mutate it before the API round-trip finishes, so
 * the cell reacts instantly. A failed round-trip rolls the local state
 * back from the server response.
 */
export function CalendarDetailPage() {
  const { calendarId: param } = useParams<{ calendarId: string }>();
  const calendarId = Number(param);
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Timezones load once (they're tenant-agnostic) and survive a failed
  // fetch: if the list can't load we degrade to an input that keeps the
  // user's existing id without offering choices, so saving name-only
  // still works.
  const [timezones, setTimezones] = useState<TimezoneOption[] | null>(null);

  // The month the calendar grid is currently focused on. Defaults to
  // today so the customer lands on a useful view; navigating the grid
  // only changes this state, not the data.
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth()); // 0-based

  const refresh = useCallback(async () => {
    if (!Number.isFinite(calendarId) || calendarId <= 0) {
      setState({ status: "error", message: "Invalid calendar id." });
      return;
    }
    try {
      const [detail, items] = await Promise.all([
        schedulesApi.getCalendar(calendarId),
        schedulesApi.listCalendarItems(calendarId),
      ]);
      setState({
        status: "ready",
        detail,
        dates: new Set(items.dates ?? []),
      });
      setDraft(toDraft(detail));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load calendar";
      setState({ status: "error", message });
    }
  }, [calendarId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const response = await schedulesApi.listTimezones();
        if (!alive) return;
        setTimezones(response.items ?? []);
      } catch {
        // Silently fall back to "no picker" — the user can still save
        // name + description, they just won't see the dropdown until the
        // server comes back.
        if (alive) setTimezones([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const isDirty = useMemo(() => {
    if (state.status !== "ready" || !draft) return false;
    // "UTC" and "" mean the same thing to the user (null timezone => UTC),
    // so collapse both to "" before comparing. That keeps the Save button
    // correctly disabled when a user only looked at a UTC calendar
    // without changing anything.
    const effective = (v: string | null | undefined) => {
      const s = v ?? "";
      return s === "UTC" ? "" : s;
    };
    return (
      draft.name.trim() !== (state.detail.name ?? "").trim() ||
      draft.description.trim() !== (state.detail.description ?? "").trim() ||
      effective(draft.timezoneId) !== effective(state.detail.timezoneId)
    );
  }, [state, draft]);

  const onSave = useCallback(async () => {
    if (state.status !== "ready" || !draft) return;
    const name = draft.name.trim();
    if (!name) {
      setSaveError("Name is required.");
      return;
    }
    setBusy(true);
    setSaveError(null);
    try {
      // Only send timezoneId when the user actually changed it; that way
      // the backend stays a pure no-op when the picker is untouched and
      // concurrent admins editing the same calendar in a different tab
      // don't clobber each other's timezone choices. "UTC" and "" are
      // equivalent (see isDirty above), so normalise before comparing.
      const normalise = (v: string | null | undefined) => {
        const s = v ?? "";
        return s === "UTC" ? "" : s;
      };
      const timezoneChanged =
        normalise(draft.timezoneId) !== normalise(state.detail.timezoneId);
      const next = await schedulesApi.updateCalendar(calendarId, {
        name,
        description: draft.description.trim(),
        ...(timezoneChanged ? { timezoneId: draft.timezoneId ?? "" } : {}),
      });
      setState({ ...state, detail: next });
      setDraft(toDraft(next));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setBusy(false);
    }
  }, [state, draft, calendarId]);

  const onToggleDate = useCallback(
    async (isoDate: string) => {
      if (state.status !== "ready") return;
      setDateError(null);
      const alreadyMarked = state.dates.has(isoDate);
      try {
        const response = alreadyMarked
          ? await schedulesApi.deleteCalendarItem(calendarId, isoDate)
          : await schedulesApi.addCalendarItem(calendarId, isoDate);
        setState({
          ...state,
          dates: new Set(response.dates ?? []),
        });
      } catch (err) {
        setDateError(
          err instanceof Error ? err.message : "Failed to update date",
        );
      }
    },
    [state, calendarId],
  );

  const performDeleteCalendar = useCallback(async () => {
    if (state.status !== "ready") return;
    setDeleteConfirmOpen(false);
    setBusy(true);
    setDeleteError(null);
    try {
      await schedulesApi.deleteCalendar(calendarId);
      navigate("/schedules");
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Failed to delete");
    } finally {
      setBusy(false);
    }
  }, [state, calendarId, navigate]);

  const onDelete = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  if (state.status === "loading") {
    return (
      <>
        <PageHeader title="Calendar" />
        <div className="dashboard">
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            Loading calendar…
          </div>
        </div>
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader title="Calendar" />
        <div className="dashboard">
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
            }}
          >
            <strong>Could not load this calendar.</strong>
            <div style={{ marginTop: 6, fontSize: "0.9rem" }}>
              {state.message}
            </div>
            <div style={{ marginTop: 12 }}>
              <Link to="/schedules" className="btn">
                <ArrowLeft size={16} /> Back to schedules
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!draft) return null;

  return (
    <>
      <PageHeader
        title={state.detail.name || "Calendar"}
        actions={
          <div style={{ display: "inline-flex", gap: 8 }}>
            <Link to="/schedules" className="btn">
              <ArrowLeft size={16} /> Back
            </Link>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSave}
              disabled={busy || !isDirty}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Save size={16} />
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        }
      />
      <div className="dashboard" style={{ display: "grid", gap: 16 }}>
        <section className="tile" aria-labelledby="cal-details">
          <header className="tile-head">
            <h3 id="cal-details">Details</h3>
          </header>
          <div style={{ display: "grid", gap: 12, maxWidth: 640 }}>
            <label style={{ display: "grid", gap: 4 }}>
              <span
                style={{
                  fontSize: "0.8rem",
                  color: "var(--portal-muted)",
                  fontWeight: 600,
                }}
              >
                Name
              </span>
              <input
                type="text"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                disabled={busy}
                maxLength={100}
                className="portal-input-inline"
                style={{
                  padding: "8px 12px",
                  border: "1px solid var(--portal-border)",
                  borderRadius: 6,
                  fontSize: "0.95rem",
                  fontFamily: "inherit",
                  background: "#ffffff",
                }}
              />
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              <span
                style={{
                  fontSize: "0.8rem",
                  color: "var(--portal-muted)",
                  fontWeight: 600,
                }}
              >
                Description
              </span>
              <textarea
                value={draft.description}
                onChange={(e) =>
                  setDraft({ ...draft, description: e.target.value })
                }
                disabled={busy}
                maxLength={500}
                rows={3}
                className="portal-input-inline"
                style={{
                  padding: "8px 12px",
                  border: "1px solid var(--portal-border)",
                  borderRadius: 6,
                  fontSize: "0.95rem",
                  fontFamily: "inherit",
                  background: "#ffffff",
                  resize: "vertical",
                }}
              />
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              <span
                style={{
                  fontSize: "0.8rem",
                  color: "var(--portal-muted)",
                  fontWeight: 600,
                }}
              >
                Timezone
              </span>
              <select
                value={draft.timezoneId ?? ""}
                onChange={(e) =>
                  setDraft({ ...draft, timezoneId: e.target.value })
                }
                // Disable while saving, and also while we're still
                // fetching the timezone list so the user can't commit an
                // id the server hasn't told us is valid yet.
                disabled={busy || timezones === null}
                className="portal-input-inline"
                style={{
                  padding: "8px 12px",
                  border: "1px solid var(--portal-border)",
                  borderRadius: 6,
                  fontSize: "0.95rem",
                  fontFamily: "inherit",
                  background: "#ffffff",
                }}
              >
                {/* Empty value (stored as NULL) is explicitly UTC in the
                    UI. We drop any server-side "UTC" entry below so we
                    don't show two UTCs; both would resolve to the same
                    thing anyway. */}
                <option value="">UTC</option>
                {/* If the server saved an id that's no longer present in
                    sys.time_zone_info (e.g. host OS removed it), surface
                    it as an option so the user still sees what's stored
                    and can consciously pick a replacement. */}
                {draft.timezoneId &&
                  timezones &&
                  !timezones.some((t) => t.id === draft.timezoneId) && (
                    <option value={draft.timezoneId}>
                      {draft.timezoneId} (not installed)
                    </option>
                  )}
                {(timezones ?? [])
                  // Deduplicate: our synthetic top option already covers
                  // UTC (value = ""). Showing a second "UTC (UTC+00:00)"
                  // entry would let a user pick a semantically-identical
                  // but technically-different value.
                  .filter((tz) => tz.id !== "UTC")
                  .map((tz) => (
                    <option key={tz.id} value={tz.id}>
                      {tz.label}
                    </option>
                  ))}
              </select>
            </label>
            {saveError && (
              <div style={{ color: "#c0392b", fontSize: "0.9rem" }}>
                {saveError}
              </div>
            )}
          </div>
        </section>

        <section className="tile" aria-labelledby="cal-dates">
          <header
            className="tile-head"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3 id="cal-dates">Marked dates</h3>
            <span style={{ color: "var(--portal-muted)", fontSize: "0.85rem" }}>
              Click a day to toggle it. Marked dates are evaluated in the
              calendar's own timezone.
            </span>
          </header>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(280px, 1fr) minmax(220px, 320px)",
              gap: 16,
              alignItems: "start",
            }}
          >
            <MonthGrid
              year={viewYear}
              month={viewMonth}
              onYearMonth={(y, m) => {
                setViewYear(y);
                setViewMonth(m);
              }}
              dates={state.dates}
              onToggle={onToggleDate}
              disabled={busy}
            />
            <MarkedDatesList
              dates={state.dates}
              onRemove={(iso) => onToggleDate(iso)}
              disabled={busy}
            />
          </div>
          {dateError && (
            <div
              style={{ marginTop: 8, color: "#c0392b", fontSize: "0.9rem" }}
            >
              {dateError}
            </div>
          )}
        </section>

        <DangerZone
          name={state.detail.name}
          busy={busy}
          error={deleteError}
          onRequestDelete={onDelete}
        />
      </div>

      {state.status === "ready" && (
        <ConfirmModal
          open={deleteConfirmOpen}
          onClose={() => {
            if (!busy) setDeleteConfirmOpen(false);
          }}
          title="Delete calendar?"
          confirmLabel="Delete"
          tone="danger"
          busy={busy}
          onConfirm={() => void performDeleteCalendar()}
        >
          Delete calendar &quot;{state.detail.name}&quot;? This cannot be
          undone.
        </ConfirmModal>
      )}
    </>
  );
}

function MonthGrid({
  year,
  month,
  onYearMonth,
  dates,
  onToggle,
  disabled,
}: {
  year: number;
  month: number; // 0-11
  onYearMonth: (year: number, month: number) => void;
  dates: Set<string>;
  onToggle: (isoDate: string) => void;
  disabled: boolean;
}) {
  // Sunday-first is the US default; we want Monday-first. Compute the
  // offset from Monday so the grid always starts on a Monday regardless
  // of which weekday the month begins on.
  const firstOfMonth = new Date(year, month, 1);
  const jsDayIndex = firstOfMonth.getDay(); // 0 = Sunday
  const mondayIndex = (jsDayIndex + 6) % 7; // 0 = Monday
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const todayIso = toIso(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  const cells: (number | null)[] = [];
  for (let i = 0; i < mondayIndex; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = firstOfMonth.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });

  const onPrev = () => {
    if (month === 0) onYearMonth(year - 1, 11);
    else onYearMonth(year, month - 1);
  };
  const onNext = () => {
    if (month === 11) onYearMonth(year + 1, 0);
    else onYearMonth(year, month + 1);
  };
  const onJumpToday = () => {
    onYearMonth(today.getFullYear(), today.getMonth());
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <button
          type="button"
          className="btn"
          onClick={onPrev}
          style={{ padding: "4px 8px" }}
          aria-label="Previous month"
        >
          <ChevronLeft size={16} />
        </button>
        <div style={{ fontWeight: 600, flex: 1, textAlign: "center" }}>
          {monthLabel}
        </div>
        <button
          type="button"
          className="btn"
          onClick={onNext}
          style={{ padding: "4px 8px" }}
          aria-label="Next month"
        >
          <ChevronRight size={16} />
        </button>
        <button
          type="button"
          className="btn"
          onClick={onJumpToday}
          style={{ padding: "4px 8px", fontSize: "0.8rem" }}
        >
          Today
        </button>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 4,
          fontSize: "0.85rem",
        }}
      >
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div
            key={d}
            style={{
              textAlign: "center",
              fontWeight: 600,
              color: "var(--portal-muted)",
              padding: "4px 0",
            }}
          >
            {d}
          </div>
        ))}
        {cells.map((cell, idx) => {
          if (cell == null) return <div key={idx} />;
          const iso = toIso(year, month, cell);
          const isMarked = dates.has(iso);
          const isToday = iso === todayIso;
          return (
            <button
              key={idx}
              type="button"
              onClick={() => onToggle(iso)}
              disabled={disabled}
              aria-pressed={isMarked}
              aria-label={`${iso}${isMarked ? " (marked)" : ""}`}
              title={iso}
              style={{
                padding: "10px 0",
                borderRadius: 6,
                border: isToday
                  ? "1px solid var(--portal-primary)"
                  : "1px solid var(--portal-border)",
                background: isMarked ? "var(--portal-primary)" : "#ffffff",
                color: isMarked
                  ? "#ffffff"
                  : isToday
                  ? "var(--portal-primary)"
                  : "var(--portal-text)",
                fontWeight: isMarked || isToday ? 700 : 500,
                cursor: disabled ? "not-allowed" : "pointer",
                fontFamily: "inherit",
                fontSize: "0.9rem",
              }}
            >
              {cell}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MarkedDatesList({
  dates,
  onRemove,
  disabled,
}: {
  dates: Set<string>;
  onRemove: (iso: string) => void;
  disabled: boolean;
}) {
  const sorted = useMemo(() => Array.from(dates).sort(), [dates]);
  return (
    <div
      style={{
        border: "1px solid var(--portal-border)",
        borderRadius: 8,
        background: "#ffffff",
        maxHeight: 360,
        overflow: "auto",
      }}
    >
      <div
        style={{
          padding: "8px 10px",
          borderBottom: "1px solid var(--portal-border)",
          fontSize: "0.8rem",
          fontWeight: 600,
          color: "var(--portal-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {sorted.length} marked {sorted.length === 1 ? "date" : "dates"}
      </div>
      {sorted.length === 0 ? (
        <div
          style={{
            padding: "12px 10px",
            color: "var(--portal-muted)",
            fontSize: "0.9rem",
          }}
        >
          No dates marked yet.
        </div>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {sorted.map((iso) => (
            <li
              key={iso}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "6px 10px",
                borderBottom: "1px solid var(--portal-border)",
                fontSize: "0.9rem",
              }}
            >
              <span>{formatHumanDate(iso)}</span>
              <button
                type="button"
                onClick={() => onRemove(iso)}
                disabled={disabled}
                aria-label={`Unmark ${iso}`}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--portal-muted)",
                  cursor: disabled ? "not-allowed" : "pointer",
                  display: "inline-flex",
                  padding: 4,
                }}
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DangerZone({
  name,
  busy,
  error,
  onRequestDelete,
}: {
  name: string;
  busy: boolean;
  error: string | null;
  onRequestDelete: () => void;
}) {
  return (
    <section className="tile" aria-labelledby="cal-danger">
      <header className="tile-head">
        <h3 id="cal-danger">Delete</h3>
      </header>
      <p style={{ color: "var(--portal-muted)", margin: "0 0 12px 0" }}>
        Deleting a calendar is permanent. Flow-builder nodes that still
        reference this calendar will no longer receive exception dates.
      </p>
      {error && (
        <div
          style={{
            marginBottom: 12,
            color: "#c0392b",
            background: "#fdecea",
            border: "1px solid #e74c3c",
            borderRadius: 8,
            padding: 10,
            fontSize: "0.9rem",
          }}
        >
          {error}
        </div>
      )}
      <button
        type="button"
        className="btn"
        onClick={onRequestDelete}
        disabled={busy}
        title={`Delete calendar "${name}"`}
        style={{
          background: "#ffffff",
          color: busy ? "var(--portal-muted)" : "#c0392b",
          borderColor: busy ? "var(--portal-border)" : "#e74c3c",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Trash2 size={16} />
        Delete calendar
      </button>
    </section>
  );
}

function toIso(year: number, month: number, day: number): string {
  // Format as yyyy-MM-dd without going through Date so daylight-saving
  // edge cases can't pull a date off by one. The API stores calendar
  // dates as date-only rows anyway.
  const mm = String(month + 1).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

function formatHumanDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
